import type { ReactNode } from "react";
import { useState } from "react";

import { Loader2, X } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/shared/components/ui/sheet";
import { cn } from "@/shared/lib/utils";

interface FormSheetProps {
  /** Estado controlado. */
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Título — display Fraunces. */
  title: string;
  /** Subtítulo opcional debajo del título. */
  description?: ReactNode;
  /** Contenido del form — por lo general un `<form>` con `id={formId}`. */
  children: ReactNode;
  /** `id` del `<form>` interno — el footer dispara submit via `form={formId}`. */
  formId: string;
  /** Copy del botón de submit. */
  submitLabel?: string;
  /** Loading del submit (típicamente `mutation.isLoading`). */
  submitting?: boolean;
  /** Disabled override (ej. form inválido). */
  submitDisabled?: boolean;
  /** `true` cuando el form tiene cambios no guardados — gatea el close. */
  dirty?: boolean;
  /** Ancho de la sheet. Default `md`. */
  size?: "md" | "lg" | "xl";
}

/**
 * Sheet lateral estandarizado para forms de create/edit.
 *
 * Layout:
 *   ┌───────────────────────────────┐
 *   │ title                       × │  header sticky
 *   │ description                   │
 *   ├───────────────────────────────┤
 *   │ children (scroll)             │
 *   ├───────────────────────────────┤
 *   │ Cancelar          [ Guardar ] │  footer sticky
 *   └───────────────────────────────┘
 *
 * Dirty guard: si `dirty` y la usuaria intenta cerrar (ESC, overlay,
 * botón X, cancelar), muestra un AlertDialog "¿descartar cambios?".
 */
export function FormSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  formId,
  submitLabel = "Guardar",
  submitting = false,
  submitDisabled = false,
  dirty = false,
  size = "md",
}: FormSheetProps) {
  const [discardOpen, setDiscardOpen] = useState(false);

  const sizes = {
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
  };

  const tryClose = () => {
    if (submitting) return;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            tryClose();
            return;
          }
          onOpenChange(next);
        }}
      >
        <SheetContent
          side="right"
          hideClose
          className={cn("flex w-full flex-col gap-0 p-0", sizes[size])}
          onEscapeKeyDown={(e) => {
            if (dirty || submitting) e.preventDefault();
            tryClose();
          }}
          onPointerDownOutside={(e) => {
            if (dirty || submitting) e.preventDefault();
            tryClose();
          }}
        >
          {/* Header */}
          <header className="flex items-start justify-between gap-4 border-b border-border/60 px-6 py-5">
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-display text-[1.35rem] font-medium tracking-display-tight text-foreground">
                {title}
              </SheetTitle>
              {description ? (
                <SheetDescription className="mt-1 text-[13px] leading-relaxed">
                  {description}
                </SheetDescription>
              ) : null}
            </div>
            <button
              type="button"
              onClick={tryClose}
              disabled={submitting}
              aria-label="Cerrar"
              className={cn(
                "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "transition-colors hover:bg-accent/40 hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

          {/* Footer sticky */}
          <footer className="flex items-center justify-end gap-2 border-t border-border/60 bg-background/80 px-6 py-4 backdrop-blur">
            <Button type="button" variant="ghost" onClick={tryClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" form={formId} disabled={submitting || submitDisabled}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando…
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </footer>
        </SheetContent>
      </Sheet>

      {/* Dirty guard */}
      <AlertDialog open={discardOpen} onOpenChange={(next) => !next && setDiscardOpen(false)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[1.25rem] font-medium tracking-display-tight">
              Descartar cambios
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13.5px] leading-relaxed">
              Tienes cambios sin guardar en el formulario. Si cierras ahora, se perderán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDiscardOpen(false)}>
              Seguir editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscardOpen(false);
                onOpenChange(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
